import { useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Square, Circle, X, Minus, Plus } from 'lucide-react';
import { TeleprompterOverlay } from './TeleprompterOverlay';
import type { DialogueTurn } from '@/utils/parseScript';

interface CameraPreviewProps {
  stream: MediaStream;
  isRecording: boolean;
  duration: number;
  script: string;
  scrollSpeed: number;
  fontSize: number;
  onScrollSpeedChange: (speed: number) => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onCancel: () => void;
  countdown?: number | null;
  // Scene Partner
  dialogueTurns?: DialogueTurn[];
  currentTurnIndex?: number;
  actorCharacter?: string;
  isReaderSpeaking?: boolean;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export function CameraPreview({
  stream,
  isRecording,
  duration,
  script,
  scrollSpeed,
  fontSize,
  onScrollSpeedChange,
  onStartRecording,
  onStopRecording,
  onCancel,
  countdown,
  dialogueTurns,
  currentTurnIndex,
  actorCharacter,
  isReaderSpeaking,
}: CameraPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const isDialogueMode = dialogueTurns && dialogueTurns.length > 0;

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  return (
    <div className="relative w-full bg-black rounded-lg overflow-hidden">
      {/* Live camera feed */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="w-full object-cover"
        style={{ maxHeight: '80vh' }}
      />

      {/* Teleprompter overlay */}
      <TeleprompterOverlay
        script={script}
        isScrolling={isRecording && !isDialogueMode}
        scrollSpeed={scrollSpeed}
        fontSize={fontSize}
        dialogueTurns={dialogueTurns}
        currentTurnIndex={currentTurnIndex}
        actorCharacter={actorCharacter}
      />

      {/* Reader speaking indicator */}
      {isReaderSpeaking && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-blue-600/80 px-3 py-1.5 rounded-full">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          <span className="text-white text-xs font-medium">Reader</span>
        </div>
      )}

      {/* Countdown overlay */}
      {countdown !== null && countdown !== undefined && countdown > 0 && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50">
          <span
            key={countdown}
            className="text-white font-bold animate-countdown"
            style={{ fontSize: '120px', lineHeight: 1 }}
          >
            {countdown}
          </span>
          <style>{`
            @keyframes countdownPop {
              0% { transform: scale(1.5); opacity: 0; }
              20% { opacity: 1; }
              100% { transform: scale(0.8); opacity: 0.6; }
            }
            .animate-countdown {
              animation: countdownPop 0.9s ease-out forwards;
            }
          `}</style>
        </div>
      )}

      {/* Scroll speed control — only in scroll mode */}
      {script.trim() && !isDialogueMode && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/60 px-2 py-1.5 rounded-full">
          <button
            onClick={() => onScrollSpeedChange(Math.max(1, scrollSpeed - 1))}
            className="text-white/70 hover:text-white p-0.5"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span className="text-white text-xs font-mono w-4 text-center">{scrollSpeed}</span>
          <button
            onClick={() => onScrollSpeedChange(Math.min(10, scrollSpeed + 1))}
            className="text-white/70 hover:text-white p-0.5"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Controls bar */}
      <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-1 p-4 bg-gradient-to-t from-black/70 to-transparent">
        {/* Voice command hint */}
        <span className="text-white/50 text-xs">
          {isRecording ? 'Say "cut" to stop' : 'Say "action" to record'}
        </span>

        <div className="flex items-center justify-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20"
            onClick={onCancel}
          >
            <X className="w-5 h-5" />
          </Button>

          {isRecording ? (
            <button
              onClick={onStopRecording}
              className="w-14 h-14 rounded-full border-4 border-white flex items-center justify-center bg-transparent hover:bg-white/10 transition-colors"
            >
              <Square className="w-6 h-6 text-red-500 fill-red-500" />
            </button>
          ) : (
            <button
              onClick={onStartRecording}
              disabled={countdown !== null && countdown !== undefined && countdown > 0}
              className="w-14 h-14 rounded-full border-4 border-white flex items-center justify-center bg-transparent hover:bg-white/10 transition-colors disabled:opacity-40"
            >
              <Circle className="w-8 h-8 text-red-500 fill-red-500" />
            </button>
          )}

          {/* Duration display when recording, spacer otherwise */}
          <div className="w-[52px] text-center">
            {isRecording && (
              <span className="text-white text-sm font-mono">{formatDuration(duration)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
